import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Terminal } from '../models/terminal.model';

@Injectable({
    providedIn: 'root'
})
export class TerminalStateService {

    private readonly STORAGE_KEY = 'selectedTerminal';
    private selectedTerminalSubject = new BehaviorSubject<Terminal | null>(null);

    constructor() {
        // Récupérer le terminal sauvegardé au démarrage
        this.loadFromStorage();
    }

    /**
     * Observable du terminal actuellement sélectionné
     */
    get selectedTerminal$(): Observable<Terminal | null> {
        return this.selectedTerminalSubject.asObservable();
    }

    /**
     * Récupère le terminal actuellement sélectionné
     */
    getSelectedTerminal(): Terminal | null {
        return this.selectedTerminalSubject.value;
    }

    /**
     * Met à jour le terminal sélectionné et le sauvegarde
     * @param terminal Terminal à sélectionner
     */
    setSelectedTerminal(terminal: Terminal | null): void {
        this.selectedTerminalSubject.next(terminal);
        this.saveToStorage(terminal);
    }

    /**
     * Sauvegarde le terminal dans le localStorage
     * @param terminal Terminal à sauvegarder
     */
    private saveToStorage(terminal: Terminal | null): void {
        try {
            if (terminal) {
                localStorage.setItem(this.STORAGE_KEY, JSON.stringify({
                    id: terminal.id,
                    identifier: terminal.identifier,
                    serialNumber: terminal.serialNumber,
                    storeChain: terminal.storeChain,
                    contractStartedAt: terminal.contractStartedAt,
                    contractDurationMonths: terminal.contractDurationMonths,
                    contractType: terminal.contractType,
                    implantedAt: terminal.implantedAt,
                    isAvailable: terminal.isAvailable,
                    isCartridgeStuck: terminal.isCartridgeStuck,
                    isBoxFull: terminal.isBoxFull,
                    paperLvl: terminal.paperLvl,
                    hasPrerequisites: terminal.hasPrerequisites,
                    lastStatusUpdated: terminal.lastStatusUpdated,
                    currentYearRank: terminal.currentYearRank,
                    createdAt: terminal.createdAt,
                    commercialId: terminal.commercialId
                }));
            } else {
                localStorage.removeItem(this.STORAGE_KEY);
            }
        } catch (error) {
            console.warn('Erreur lors de la sauvegarde du terminal sélectionné:', error);
        }
    }

    /**
     * Charge le terminal depuis le localStorage
     */
    private loadFromStorage(): void {
        try {
            const stored = localStorage.getItem(this.STORAGE_KEY);
            if (stored) {
                const terminalData = JSON.parse(stored);
                // Créer un objet Terminal basique pour la comparaison
                const terminal = new Terminal(
                    terminalData.id || terminalData.identifier, // Rétrocompatibilité
                    terminalData.serialNumber,
                    terminalData.identifier,
                    terminalData.contractStartedAt,
                    terminalData.contractDurationMonths,
                    terminalData.contractType,
                    terminalData.implantedAt,
                    terminalData.storeChain,
                    terminalData.isAvailable,
                    terminalData.isCartridgeStuck,
                    terminalData.isBoxFull,
                    terminalData.paperLvl,
                    terminalData.hasPrerequisites,
                    terminalData.lastStatusUpdated,
                    terminalData.currentYearRank,
                    terminalData.createdAt,
                    terminalData.commercialId,
                    undefined, // commercial
                    undefined  // user
                );
                this.selectedTerminalSubject.next(terminal);
            }
        } catch (error) {
            console.warn('Erreur lors du chargement du terminal sauvegardé:', error);
            localStorage.removeItem(this.STORAGE_KEY);
        }
    }

    /**
     * Efface le terminal sauvegardé (utile lors de la déconnexion)
     */
    clearSavedTerminal(): void {
        localStorage.removeItem(this.STORAGE_KEY);
        this.selectedTerminalSubject.next(null);
    }
}
